import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { useLocation } from "react-router-dom";
import type { OrganizationWithRole } from "../types/organization";

interface OrganizationContextValue {
  organizations: OrganizationWithRole[];
  activeOrganization: OrganizationWithRole | null;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  switchOrganization: (orgId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

/** Max automatic retry attempts when the organization fetch fails */
const MAX_RETRIES = 3;
/** Base delay between retries — actual delay is BASE * attemptNumber (2s, 4s, 6s) */
const RETRY_BASE_DELAY = 2000;

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { token, authenticated, signalAuthExpired } = useAuth();
  const location = useLocation();
  const [organizations, setOrganizations] = useState<OrganizationWithRole[]>([]);
  const [activeOrganization, setActiveOrganization] = useState<OrganizationWithRole | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  // Start as false — only set to true when an actual fetch is in progress.
  // This prevents ContextGuard from showing a loading screen before auth completes.
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generation counter to prevent stale updates from superseded fetch chains
  const fetchGenRef = useRef(0);

  const selectActiveOrganization = (orgs: OrganizationWithRole[]): string => {
    // Priority: URL param → localStorage → first org (API returns last-active first)
    const urlParams = new URLSearchParams(location.search);
    const urlOrgId = urlParams.get('org');
    if (urlOrgId && orgs.find(o => o.id === urlOrgId)) {
      return urlOrgId;
    }

    const storedOrgId = localStorage.getItem('activeOrganizationId');
    if (storedOrgId && orgs.find(o => o.id === storedOrgId)) {
      return storedOrgId;
    }

    return orgs[0].id;
  };

  const fetchOrganizations = async (attempt = 0) => {
    if (!token || !authenticated) return;

    // Each fresh call (attempt === 0) increments generation; retries keep the same generation.
    const generation = attempt === 0 ? ++fetchGenRef.current : fetchGenRef.current;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch('/api/organization', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      // Bail out if a newer fetch chain has started
      if (fetchGenRef.current !== generation) return;

      // 401/403 means our token has expired or been revoked. Don't retry —
      // retrying with the same dead token just burns cycles and leaves the
      // user staring at the splash screen. Signal the auth layer to redirect.
      if (response.status === 401 || response.status === 403) {
        console.warn('OrganizationProvider: got', response.status, '— triggering re-auth');
        setIsLoading(false);
        setError(null);
        signalAuthExpired();
        return;
      }

      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }

      const orgs = await response.json() as OrganizationWithRole[];

      // Auto-create a default personal organization for brand-new users
      if (orgs.length === 0) {
        const createResponse = await fetch('/api/organization/ensure-default', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (fetchGenRef.current !== generation) return;

        if (createResponse.status === 401 || createResponse.status === 403) {
          console.warn('OrganizationProvider: ensure-default got', createResponse.status, '— triggering re-auth');
          setIsLoading(false);
          setError(null);
          signalAuthExpired();
          return;
        }

        if (!createResponse.ok) {
          throw new Error('Failed to create default organization');
        }

        const newOrg = await createResponse.json() as OrganizationWithRole;
        orgs.push(newOrg);
      }

      if (fetchGenRef.current !== generation) return;

      setOrganizations(orgs);

      // Select and activate the best organization
      const activeOrgId = selectActiveOrganization(orgs);
      const activeOrg = orgs.find(o => o.id === activeOrgId);

      if (activeOrg) {
        setActiveOrganization(activeOrg);
        (window as unknown as Record<string, string>).__HAVEN_ORG_ID__ = activeOrg.id;

        window.dispatchEvent(new CustomEvent('haven:org-ready', {
          detail: {
            orgId: activeOrg.id,
            orgName: activeOrg.name,
            role: activeOrg.role,
            timestamp: Date.now(),
          },
        }));
      }

      setIsInitialized(true);
      setIsLoading(false);
    } catch (err) {
      // Bail out if a newer fetch chain has started
      if (fetchGenRef.current !== generation) return;

      console.error('OrganizationProvider: fetch failed', err);

      // Retry with linear backoff
      if (attempt < MAX_RETRIES) {
        const delay = RETRY_BASE_DELAY * (attempt + 1);
        console.log(`OrganizationProvider: retrying in ${delay}ms (${attempt + 1}/${MAX_RETRIES})`);
        setTimeout(() => {
          if (fetchGenRef.current === generation) {
            fetchOrganizations(attempt + 1);
          }
        }, delay);
        // Keep isLoading=true during retries — don't surface an error yet
        return;
      }

      // All retries exhausted — surface the error
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
      setIsInitialized(true);
      setIsLoading(false);

      window.dispatchEvent(new CustomEvent('haven:org-ready', {
        detail: {
          orgId: null,
          orgName: null,
          role: null,
          timestamp: Date.now(),
          error: err instanceof Error ? err.message : 'Unknown error',
        },
      }));
    }
  };

  const switchOrganization = async (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (!org) throw new Error('Organization not found');

    setActiveOrganization(org);

    // 1. Window global for MFEs
    (window as unknown as Record<string, string>).__HAVEN_ORG_ID__ = org.id;

    // 2. Custom event for MFEs
    window.dispatchEvent(new CustomEvent('haven:org-ready', {
      detail: { orgId: org.id, orgName: org.name, role: org.role, timestamp: Date.now() },
    }));

    // 3. localStorage for persistence
    localStorage.setItem('activeOrganizationId', org.id);

    // 4. Backend update (non-blocking)
    try {
      await fetch('/api/user/active-organization', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ org_id: org.id }),
      });
    } catch {
      // Non-critical — silently ignore
    }
  };

  const refreshOrganizations = async () => {
    await fetchOrganizations(0);
  };

  useEffect(() => {
    // Admin routes skip org initialization entirely
    if (location.pathname.startsWith('/admin')) {
      setIsInitialized(true);
      setIsLoading(false);
      return;
    }

    if (authenticated && token) {
      fetchOrganizations(0);
    }
  }, [authenticated, token, location.pathname]);

  const value: OrganizationContextValue = {
    organizations,
    activeOrganization,
    isInitialized,
    isLoading,
    error,
    switchOrganization,
    refreshOrganizations,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization(): OrganizationContextValue {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
}
